/**
 * CodeViewer — Generic editor-style code viewer.
 * Zero external dependencies. Features:
 *  - Collapsible, syntax-coloured JSON tree view
 *  - Raw text view with line numbers + gutter
 *  - Tree / Raw toggle (JSON only)
 *  - Multi-tab (JSON / XML / any language)
 *  - Copy to clipboard
 *  - Full-screen portal (Esc to close)
 *  - Collapsed preview bar
 *  - Editable textarea mode
 *  - Light & dark theme aware
 */
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  ReactNode,
  memo,
  useContext,
  createContext,
} from 'react';
import { createPortal } from 'react-dom';
import {
  FiCopy,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiMaximize2,
  FiTerminal,
  FiX,
  FiCode,
  FiList,
  FiSearch,
} from 'react-icons/fi';
import { VscJson, VscFileCode } from 'react-icons/vsc';
import { GRADIENT } from '@/components/organism/TableExpand/primitives';
import { AppButton } from '@/components/atoms/AppButton';
import { Text } from '@/components/atoms/AppText';
import { copyToClipboard } from '@/utils/copyToClipboard';
import {
  APP_BG,
  APP_BORDER,
  APP_TEXT,
  APP_CODE_SYNTAX,
} from '@/constant/theme/colors.constant';
import ServiceCard from '@/components/organism/Cards/ServiceCard';
import { tid } from '@/utils/testId';
import { ORGANISM_TEST_IDS } from '@/constant/testIds/oganism.testIds';

export type CodeLanguage = 'json' | 'xml' | 'text';

export interface CodeViewerTab {
  key: string;
  label: string;
  content: string;
  language?: CodeLanguage;
  dotClass?: string;
  icon?: React.ReactElement;
}

export interface CodeViewerProps {
  content?: string;
  tabs?: CodeViewerTab[];
  defaultTab?: string;
  activeTab?: string;
  onTabChange?: (key: string) => void;
  language?: CodeLanguage;
  filename?: string;
  maxLines?: number;
  defaultOpen?: boolean;
  cardLabel?: string;
  cardIcon?: React.ReactElement;
  className?: string;
  editable?: boolean;
  onContentChange?: (value: string) => void;
  footer?: ReactNode;
  placeholder?: string;
  showSearch?: boolean;
  testId?: string;
}

// --- Language defaults ---

const LANG_DEFAULTS: Record<
  CodeLanguage,
  { dotClass: string; icon: React.ReactElement; ext: string }
> = {
  json: {
    dotClass: 'bg-amber-400',
    icon: <VscJson className="h-3 w-3" />,
    ext: 'json',
  },
  xml: {
    dotClass: 'bg-cyan-400',
    icon: <VscFileCode className="h-3 w-3" />,
    ext: 'xml',
  },
  text: {
    dotClass: 'bg-gray-400',
    icon: <VscFileCode className="h-3 w-3" />,
    ext: 'txt',
  },
};

function getLangDefaults(lang: CodeLanguage = 'text') {
  return LANG_DEFAULTS[lang] ?? LANG_DEFAULTS.text;
}

interface ResolvedTab {
  key: string;
  label: string;
  content: string;
  language: CodeLanguage;
  dotClass: string;
  icon: React.ReactElement;
}

function buildTab(tab: CodeViewerTab): ResolvedTab {
  const def = getLangDefaults(tab.language);
  return {
    key: tab.key,
    label: tab.label,
    content: tab.content,
    language: tab.language ?? 'text',
    dotClass: tab.dotClass ?? def.dotClass,
    icon: (tab.icon ?? def.icon) as React.ReactElement,
  };
}

// --- JSON Tree ---

type JsonPrimitive = string | number | boolean | null;
interface JsonObject {
  [key: string]: JsonValue;
}
type JsonArray = JsonValue[];
type JsonValue = JsonPrimitive | JsonObject | JsonArray;

const T = APP_CODE_SYNTAX;

const INDENT = 1.2;

const SearchContext = createContext('');
const { CODE_VIEWER_TID } = ORGANISM_TEST_IDS;

const Highlight = ({ text, query }: { text: string; query: string }) => {
  if (!query) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  const lower = text.toLowerCase();
  const lowerQ = query.toLowerCase();
  let last = 0;
  let i = lower.indexOf(lowerQ, last);
  while (i !== -1) {
    if (i > last) parts.push(text.slice(last, i));
    parts.push(
      <mark key={String(i)} className="bg-yellow-200 dark:bg-yellow-500/40 text-inherit rounded-[2px] not-italic">
        {text.slice(i, i + query.length)}
      </mark>,
    );
    last = i + query.length;
    i = lower.indexOf(lowerQ, last);
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

interface JsonNodeProps {
  value: JsonValue;
  keyName?: string;
  depth?: number;
  isLast?: boolean;
  startCollapsed?: boolean;
}

const JsonNode = memo(function JsonNode({
  value,
  keyName,
  depth = 0,
  isLast = true,
  startCollapsed = false,
}: JsonNodeProps) {
  const [open, setOpen] = useState(!startCollapsed);
  const searchQuery = useContext(SearchContext);
  useEffect(() => {
    if (searchQuery) setOpen(true);
    else if (startCollapsed) setOpen(false);
  }, [searchQuery, startCollapsed]);
  const isObj =
    value !== null && typeof value === 'object' && !Array.isArray(value);
  const isArr = Array.isArray(value);

  const entries: [string, JsonValue][] = isObj
    ? Object.entries(value as Record<string, JsonValue>)
    : isArr
      ? (value as JsonValue[]).map((v, i) => [String(i), v])
      : [];

  const ob = isArr ? '[' : '{';
  const cb = isArr ? ']' : '}';
  const comma = isLast ? '' : ',';
  const isComplex = isObj || isArr;

  const pad = (
    <span style={{ display: 'inline-block', width: `${depth * INDENT}rem` }} />
  );

  const keyEl =
    keyName !== undefined && !/^\d+$/.test(keyName) ? (
      <>
        <span className={T.key}>"<Highlight text={keyName} query={searchQuery} />"</span>
        <span className={T.bracket}>: </span>
      </>
    ) : null;

  if (!isComplex) {
    let val: React.ReactNode;
    if (value === null) val = <span className={T.nil}>null</span>;
    else if (typeof value === 'boolean')
      val = <span className={T.bool}><Highlight text={String(value)} query={searchQuery} /></span>;
    else if (typeof value === 'number')
      val = <span className={T.num}><Highlight text={String(value)} query={searchQuery} /></span>;
    else val = <span className={T.str}>"<Highlight text={value as string} query={searchQuery} />"</span>;
    return (
      <div className="leading-[1.75]">
        {pad}
        {keyEl}
        {val}
        <span className={T.bracket}>{comma}</span>
      </div>
    );
  }

  return (
    <div>
      <div
        className="leading-[1.75] flex items-center group cursor-pointer select-none"
        onClick={() => setOpen((o) => !o)}
      >
        {pad}
        <span
          className={`inline-flex items-center justify-center w-3.5 h-3.5 mr-0.5 transition-colors ${open ? 'text-gray-400 dark:text-gray-600' : 'text-primary/60'} group-hover:text-primary`}
        >
          {open ? (
            <svg viewBox="0 0 10 7" className="w-2.5 h-2 fill-current">
              <path d="M0 0l5 7 5-7z" />
            </svg>
          ) : (
            <svg viewBox="0 0 7 10" className="w-2 h-2.5 fill-current">
              <path d="M0 0l7 5-7 5z" />
            </svg>
          )}
        </span>
        {keyEl}
        <span className={T.bracket}>{ob}</span>
        {!open && (
          <>
            <span
              className={`ml-1.5 text-[10px] font-medium px-1.5 py-0 rounded-full ${T.count}`}
            >
              {entries.length}
              {isArr ? ' items' : ' keys'}
            </span>
            <span className={T.bracket}>
              {cb}
              {comma}
            </span>
          </>
        )}
      </div>

      {open && (
        <div
          style={{ paddingLeft: `${(depth + 1) * INDENT}rem` }}
          className="relative"
        >
          <span
            className="absolute top-0 bottom-0 border-l border-gray-200 dark:border-white/[0.08]"
            style={{ left: `${depth * INDENT + 0.65}rem` }}
          />
          {entries.map(([k, v], idx) => (
            <JsonNode
              key={k}
              value={v}
              keyName={isArr ? undefined : k}
              depth={depth + 1}
              isLast={idx === entries.length - 1}
              startCollapsed={depth >= 2}
            />
          ))}
        </div>
      )}

      {open && (
        <div className="leading-[1.75]">
          {pad}
          <span className={T.bracket}>
            {cb}
            {comma}
          </span>
        </div>
      )}
    </div>
  );
});

const JsonTree = ({
  content,
  fullscreen = false,
}: {
  content: string;
  fullscreen?: boolean;
}) => {
  const parsed = useMemo<{ ok: true; data: JsonValue } | { ok: false }>(() => {
    try {
      return { ok: true, data: JSON.parse(content) };
    } catch {
      return { ok: false };
    }
  }, [content]);

  if (!parsed.ok) return <CodePane content={content} fullscreen={fullscreen} />;

  return (
    <div
      className={`overflow-auto px-5 py-3 font-mono text-[11.5px] select-text ${fullscreen ? 'h-full' : 'max-h-[480px]'}`}
    >
      <JsonNode value={parsed.data} depth={0} />
    </div>
  );
};

// --- ViewToggle ---

type ViewMode = 'tree' | 'raw';

const ViewToggle = ({
  mode,
  onChange,
}: {
  mode: ViewMode;
  onChange: (m: ViewMode) => void;
}) => {
  return (
    <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]">
      {(['tree', 'raw'] as ViewMode[]).map((m) => {
        const active = mode === m;
        return (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m)}
            className={[
              'flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-all select-none',
              active
                ? 'text-white shadow-sm'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300',
            ].join(' ')}
            style={active ? { background: GRADIENT } : undefined}
          >
            {m === 'tree' ? (
              <FiList className="h-3 w-3" />
            ) : (
              <FiCode className="h-3 w-3" />
            )}
            {m === 'tree' ? 'Tree' : 'Raw'}
          </button>
        );
      })}
    </div>
  );
};

// --- TabBtn ---

const TabBtn = ({
  active,
  dotClass,
  icon,
  onClick,
  children,
}: {
  active: boolean;
  dotClass: string;
  icon: React.ReactElement;
  onClick: () => void;
  children: ReactNode;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'relative flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-[11px] font-medium transition-all duration-150 select-none whitespace-nowrap',
        active
          ? 'text-white shadow-sm'
          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-white/[0.06]',
      ].join(' ')}
      style={active ? { background: GRADIENT } : undefined}
    >
      {icon}
      {children}
    </button>
  );
};

// --- CodePane ---

const CodePane = ({
  content,
  maxLines = 400,
  fullscreen = false,
}: {
  content: string;
  maxLines?: number;
  fullscreen?: boolean;
}) => {
  const searchQuery = useContext(SearchContext);
  const lines = useMemo(
    () => content.split('\n').slice(0, maxLines),
    [content, maxLines],
  );
  const gutterW = String(lines.length).length;
  return (
    <div
      className={`group flex overflow-auto font-mono text-[11.5px] leading-[1.7] select-text ${fullscreen ? 'h-full' : 'max-h-[480px]'}`}
    >
      <div
        className={`sticky left-0 flex-shrink-0 select-none border-r border-gray-200 dark:border-white/[0.05] ${APP_BG.codeMetaBlur} backdrop-blur-sm`}
        style={{ minWidth: `${gutterW * 0.6 + 2}rem` }}
      >
        {lines.map((_, i) => (
          <div
            key={i}
            className="px-3 text-right text-gray-300 dark:text-gray-700 leading-[1.7] tabular-nums"
          >
            {i + 1}
          </div>
        ))}
      </div>
      <pre
        className={`flex-1 min-w-0 px-5 py-0 ${APP_TEXT.code} whitespace-pre-wrap leading-[1.7]`}
        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
      >
        {lines.map((line, i) => {
          const isMatch = !!searchQuery && line.toLowerCase().includes(searchQuery.toLowerCase());
          // Measure leading spaces/tabs to compute hanging-indent for wrapped lines
          const indent = line.match(/^(\s*)/)?.[1]?.length ?? 0;
          return (
            <div
              key={i}
              className={`transition-colors duration-75 rounded-sm px-1 -mx-1 ${isMatch ? 'bg-yellow-100/80 dark:bg-yellow-500/15' : 'hover:bg-primary/[0.04] dark:hover:bg-white/[0.03]'}`}
              style={indent > 0 ? { paddingLeft: `${indent}ch`, textIndent: `-${indent}ch` } : undefined}
            >
              <Highlight text={line || '\u00A0'} query={searchQuery} />
            </div>
          );
        })}
      </pre>
    </div>
  );
};

// --- EditablePane ---

const EditablePane = ({
  content,
  onChange,
  placeholder,
  footer,
}: {
  content: string;
  onChange: (v: string) => void;
  placeholder?: string;
  footer?: ReactNode;
}) => {
  return (
    <div className={`${APP_BG.code} p-4`}>
      <textarea
        value={content}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        className={[
          'w-full min-h-[24rem] resize-none rounded-xl font-mono text-[11.5px] leading-[1.7]',
          'p-4 outline-none transition-all duration-150',
          `${APP_BG.codeArea} ${APP_TEXT.code}`,
          'border border-gray-200 dark:border-white/[0.08]',
          'focus:border-primary/50 dark:focus:border-primary/40',
          'focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary/10',
          'placeholder-gray-300 dark:placeholder-gray-700',
        ].join(' ')}
        data-testid={tid(CODE_VIEWER_TID.EDITABLE_TEXTAREA)}
      />
      {footer && <div className="mt-2">{footer}</div>}
    </div>
  );
};

// --- EditorTitleBar ---

const EditorTitleBar = ({
  dotClass,
  langIcon,
  filename,
  lineCount,
  byteSize,
}: {
  dotClass: string;
  langIcon: React.ReactElement;
  filename: string;
  lineCount: number;
  byteSize: number;
}) => {
  return (
    <div
      className={`flex items-center justify-between px-4 py-1.5 border-b border-gray-200 dark:border-white/[0.05] ${APP_BG.codeMeta}`}
    >
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/70 dark:bg-red-500/50" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70 dark:bg-amber-500/50" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-400/70 dark:bg-green-500/50" />
        </span>
        <span className="w-px h-3 bg-gray-200 dark:bg-white/[0.08] mx-1" />
        <div className="flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
          {langIcon}
          <Text as="span" size="xs" color="muted" className="font-mono">
            {filename}
          </Text>
        </div>
      </div>
      <Text
        as="span"
        size="xs"
        color="muted"
        className="font-mono tabular-nums"
      >
        {lineCount} lines &middot;{' '}
        {byteSize < 1024
          ? `${byteSize} B`
          : `${(byteSize / 1024).toFixed(1)} KB`}
      </Text>
    </div>
  );
};

// --- FullscreenPortal ---

interface FSProps {
  content: string;
  filename: string;
  dotClass: string;
  langIcon: React.ReactElement;
  langLabel: string;
  lineCount: number;
  byteSize: number;
  tabs: ResolvedTab[];
  activeTabKey: string;
  onTabChange: (k: string) => void;
  onClose: () => void;
  onCopy: () => void;
  copied: boolean;
  isJson: boolean;
  editable?: boolean;
  onContentChange?: (v: string) => void;
  footer?: ReactNode;
  placeholder?: string;
  showSearch?: boolean;
}

function FullscreenPortal({
  content,
  filename,
  dotClass,
  langIcon,
  langLabel,
  lineCount,
  byteSize,
  tabs,
  activeTabKey,
  onTabChange,
  onClose,
  onCopy,
  copied,
  isJson,
  editable = false,
  onContentChange,
  footer,
  placeholder,
  showSearch = false,
}: FSProps) {
  const [vm, setVm] = useState<ViewMode>('tree');
  const [fsSearch, setFsSearch] = useState('');

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex flex-col ${APP_BG.code}`}
      style={{ fontFamily: 'inherit' }}
    >
      <div
        className={`flex-shrink-0 flex items-center justify-between gap-3 px-4 h-12 border-b border-gray-200 dark:border-white/[0.06] ${APP_BG.codeMeta}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span className="h-3 w-3 rounded-full bg-red-400/70 dark:bg-red-500/50" />
            <span className="h-3 w-3 rounded-full bg-amber-400/70 dark:bg-amber-500/50" />
            <span
              className="h-3 w-3 rounded-full bg-green-400/70 dark:bg-green-500/50 cursor-pointer"
              onClick={onClose}
              title="Close"
            />
          </span>
          <span className="w-px h-4 bg-gray-200 dark:bg-white/[0.08] flex-shrink-0" />
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${dotClass}`}
            />
            {langIcon}
            <Text
              as="span"
              size="xs"
              color="muted"
              className="font-mono truncate"
            >
              {filename}
            </Text>
          </div>
          {!editable && showSearch && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-gray-200 dark:border-white/[0.08] bg-white/[0.04] dark:bg-white/[0.02]">
              <FiSearch className="h-3 w-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <input
                type="text"
                value={fsSearch}
                onChange={(e) => setFsSearch(e.target.value)}
                placeholder="Search…"
                className="w-[140px] bg-transparent text-[11px] font-mono text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 outline-none"
              />
              {fsSearch && (
                <button
                  type="button"
                  onClick={() => setFsSearch('')}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <FiX className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {tabs.length > 1 && (
            <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]">
              {tabs.map((tab) => (
                <TabBtn
                  key={tab.key}
                  active={activeTabKey === tab.key}
                  dotClass={tab.dotClass}
                  icon={tab.icon}
                  onClick={() => onTabChange(tab.key)}
                >
                  {tab.label}
                </TabBtn>
              ))}
            </div>
          )}
          {isJson && !editable && <ViewToggle mode={vm} onChange={setVm} />}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]">
            <FiTerminal className="h-3 w-3 text-gray-400 dark:text-gray-600" />
            <Text
              as="span"
              size="xs"
              color="muted"
              className="font-mono tabular-nums"
            >
              {lineCount}L &middot;{' '}
              {byteSize < 1024
                ? `${byteSize}B`
                : `${(byteSize / 1024).toFixed(1)}KB`}
            </Text>
          </div>
          <AppButton
            variant={copied ? 'success' : 'neutral'}
            size="xs"
            ripple={false}
            icon={copied ? <FiCheck /> : <FiCopy />}
            onClick={onCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </AppButton>
          <AppButton
            variant="ghost"
            size="sm"
            shape="square"
            ripple={false}
            icon={<FiX />}
            title="Close (Esc)"
            onClick={onClose}
          />
        </div>
      </div>
      <SearchContext.Provider value={fsSearch}>
        <div className="flex-1 overflow-hidden flex flex-col">
          {editable ? (
            <div className="flex-1 flex flex-col p-4 gap-2 overflow-auto">
              <textarea
                value={content}
                autoFocus
                spellCheck={false}
                placeholder={placeholder}
                onChange={(e) => onContentChange?.(e.target.value)}
                className={[
                  `flex-1 w-full resize-none rounded-xl font-mono text-[11.5px] leading-[1.7] min-h-0`,
                  'p-4 outline-none transition-all',
                  `${APP_BG.codeArea} ${APP_TEXT.code}`,
                  'border border-gray-200 dark:border-white/[0.08]',
                  'focus:border-primary/50 focus:ring-2 focus:ring-primary/20',
                ].join(' ')}
                data-testid={tid(CODE_VIEWER_TID.EDITABLE_TEXTAREA_FULLSCREEN)}

              />
              {footer && <div>{footer}</div>}
            </div>
          ) : isJson && vm === 'tree' ? (
            <JsonTree content={content} fullscreen />
          ) : (
            <CodePane content={content} maxLines={99999} fullscreen />
          )}
        </div>
      </SearchContext.Provider>
      <div
        className={`flex-shrink-0 flex items-center gap-4 px-4 h-6 border-t border-gray-200 dark:border-white/[0.05] ${APP_BG.codeMeta}`}
      >
        <div className="flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
          <Text as="span" size="xs" color="muted" className="font-mono">
            {langLabel}
          </Text>
        </div>
        <span className="w-px h-3 bg-gray-200 dark:bg-white/[0.08]" />
        <Text
          as="span"
          size="xs"
          color="muted"
          className="font-mono tabular-nums"
        >
          {lineCount} lines
        </Text>
        {isJson && !editable && (
          <>
            <span className="w-px h-3 bg-gray-200 dark:bg-white/[0.08]" />
            <Text as="span" size="xs" color="muted" className="font-mono">
              {vm === 'tree' ? 'Tree view' : 'Raw view'}
            </Text>
          </>
        )}
        <Text as="span" size="xs" color="muted" className="font-mono ml-auto">
          Esc to close
        </Text>
      </div>
    </div>,
    document.body,
  );
}

// --- CodeViewer (main export) ---

export const CodeViewer = ({
  content: contentProp,
  tabs: tabsProp,
  defaultTab,
  activeTab: activeTabProp,
  onTabChange: onTabChangeProp,
  language = 'json',
  filename: filenameProp,
  maxLines = 400,
  defaultOpen = false,
  cardLabel,
  cardIcon,
  className = '',
  editable = false,
  onContentChange,
  footer,
  placeholder,
  showSearch = false,
  testId
}: CodeViewerProps) => {
  const tabs: ResolvedTab[] = useMemo(() => {
    if (tabsProp && tabsProp.length > 0) return tabsProp.map(buildTab);
    const lang = getLangDefaults(language);
    return [
      {
        key: 'default',
        label: language.toUpperCase(),
        content: contentProp ?? '',
        language,
        dotClass: lang.dotClass,
        icon: lang.icon,
      },
    ];
  }, [tabsProp, contentProp, language]);

  const isControlled = activeTabProp !== undefined;
  const [internalTab, setInternalTab] = useState(
    defaultTab ?? tabs[0]?.key ?? 'default',
  );
  const activeTabKey = isControlled ? (activeTabProp as string) : internalTab;

  const handleTabChange = useCallback(
    (key: string) => {
      if (!isControlled) setInternalTab(key);
      onTabChangeProp?.(key);
    },
    [isControlled, onTabChangeProp],
  );

  const activeTab = tabs.find((t) => t.key === activeTabKey) ?? tabs[0];

  const [open, setOpen] = useState(editable ? true : defaultOpen);
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const [searchQuery, setSearchQuery] = useState('');

  const content = activeTab?.content ?? '';
  const lineCount = useMemo(() => content.split('\n').length, [content]);
  const byteSize = useMemo(() => new Blob([content]).size, [content]);
  const langDef = getLangDefaults(activeTab?.language);
  const dotClass = activeTab?.dotClass ?? langDef.dotClass;
  const langIcon = activeTab?.icon ?? langDef.icon;
  const langLabel =
    activeTab?.label ?? (activeTab?.language ?? 'text').toUpperCase();
  const filename = filenameProp ?? `payload.${langDef.ext}`;
  const isJson = (activeTab?.language ?? language) === 'json';

  const handleCopy = useCallback(() => {
    copyToClipboard(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [content]);

  const showTabs = tabs.length > 1;

  const controlsBar = (
    <div className={`border-b ${APP_BORDER.soft}`}>
      {/* ── Main row: tabs · [view toggle sm+] · [search lg+] · stats · buttons ── */}
      <div className="flex items-center gap-2 px-3 py-1.5">
        {/* Left: scrollable tabs/lang only — nothing else so it scrolls cleanly */}
        <div className="flex items-center flex-1 min-w-0 overflow-x-auto scrollbar-none">
          {showTabs ? (
            <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] flex-shrink-0">
              {tabs.map((tab) => (
                <TabBtn
                  key={tab.key}
                  active={activeTabKey === tab.key}
                  dotClass={tab.dotClass}
                  icon={tab.icon}
                  onClick={() => handleTabChange(tab.key)}
                >
                  {tab.label}
                </TabBtn>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] flex-shrink-0">
              <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
              {langIcon}
              <Text as="span" size="xs" color="muted" className="font-mono font-medium">
                {langLabel}
              </Text>
            </div>
          )}
          {/* View toggle inline on sm+ only */}
          {isJson && !editable && open && (
            <div className="hidden sm:flex flex-shrink-0 ml-1.5">
              <ViewToggle mode={viewMode} onChange={setViewMode} />
            </div>
          )}
        </div>

        {/* Search inline — desktop lg+ only */}
        {open && !editable && showSearch && (
          <div className="hidden lg:flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02] flex-1 min-w-0 max-w-[260px]">
            <FiSearch className="h-3 w-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search…"
              className="w-full bg-transparent text-[11px] font-mono text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 outline-none"
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')} className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <FiX className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {/* Right: [view toggle mobile] · stats · action buttons — all pinned */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* View toggle on mobile only (< sm) — pinned so tabs can scroll freely */}
          {isJson && !editable && open && (
            <div className="sm:hidden">
              <ViewToggle mode={viewMode} onChange={setViewMode} />
            </div>
          )}
          <div className="hidden md:flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] mx-1">
            <FiTerminal className="h-3 w-3 text-gray-400 dark:text-gray-600" />
            <Text as="span" size="xs" color="muted" className="font-mono tabular-nums">
              {lineCount}L &middot;{' '}
              {byteSize < 1024 ? `${byteSize}B` : `${(byteSize / 1024).toFixed(1)}KB`}
            </Text>
          </div>
          <AppButton
            variant={copied ? 'success' : 'neutral'}
            size="xs"
            ripple={false}
            icon={copied ? <FiCheck /> : <FiCopy />}
            onClick={handleCopy}
            dataTestId={tid(testId ? `${testId}-${CODE_VIEWER_TID.COPY_BTN}` : CODE_VIEWER_TID.COPY_BTN)}
          >
            <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
          </AppButton>
          {open && (
            <AppButton
              variant="ghost"
              size="sm"
              shape="square"
              ripple={false}
              icon={<FiMaximize2 />}
              title="Full screen"
              onClick={() => setFullscreen(true)}
              dataTestId={tid(testId ? `${testId}-${CODE_VIEWER_TID.FULL_SCREEN_BTN}` : CODE_VIEWER_TID.FULL_SCREEN_BTN)}
            />
          )}
          {!editable && (
            <AppButton
              variant="ghost"
              size="sm"
              shape="square"
              ripple={false}
              icon={open ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setOpen((p) => !p)}
              dataTestId={tid(testId ? `${testId}-${CODE_VIEWER_TID.EXPAND_BTN}` : CODE_VIEWER_TID.EXPAND_BTN)}
            />
          )}
        </div>
      </div>

      {/* ── Search row — mobile/tablet only (< lg), full width ── */}
      {open && !editable && showSearch && (
        <div className="lg:hidden px-3 pb-2">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02] w-full">
            <FiSearch className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search in file…"
              className="flex-1 min-w-0 bg-transparent text-[11.5px] font-mono text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="flex-shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.08] transition-colors"
              >
                <FiX className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const collapsedBar = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-white/[0.02] ${APP_BG.canvas} border-b ${APP_BORDER.soft} group`}
    >
      <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
      <Text as="span" size="xs" color="muted" className="font-mono">
        {filename}
      </Text>
      <span className="mx-1 text-gray-300 dark:text-gray-700">&middot;</span>
      <Text
        as="span"
        size="xs"
        color="muted"
        className="font-mono tabular-nums"
      >
        {lineCount} lines &middot;{' '}
        {byteSize < 1024
          ? `${byteSize} B`
          : `${(byteSize / 1024).toFixed(1)} KB`}
      </Text>
      <span className="ml-auto flex items-center gap-1 text-xs text-primary opacity-70 group-hover:opacity-100 transition-opacity">
        View <FiChevronDown className="h-3 w-3" />
      </span>
    </button>
  );

  const editorPanel = (
    <SearchContext.Provider value={searchQuery}>
      <div className={`overflow-hidden rounded-b-xl ${APP_BG.code}`}>
        <EditorTitleBar
          dotClass={dotClass}
          langIcon={langIcon}
          filename={filename}
          lineCount={lineCount}
          byteSize={byteSize}
        />
        {editable ? (
          <EditablePane
            content={content}
            onChange={(v) => onContentChange?.(v)}
            placeholder={placeholder}
            footer={footer}
          />
        ) : isJson && viewMode === 'tree' ? (
          <JsonTree content={content} />
        ) : (
          <CodePane content={content} maxLines={maxLines} />
        )}
      </div>
    </SearchContext.Provider>
  );

  const body = (
    <div className={className}>
      {controlsBar}
      {!open && collapsedBar}
      {open && editorPanel}
      {fullscreen && (
        <FullscreenPortal
          content={content}
          filename={filename}
          dotClass={dotClass}
          langIcon={langIcon}
          langLabel={langLabel}
          lineCount={lineCount}
          byteSize={byteSize}
          tabs={tabs}
          activeTabKey={activeTabKey}
          onTabChange={handleTabChange}
          onClose={() => setFullscreen(false)}
          onCopy={handleCopy}
          copied={copied}
          isJson={isJson}
          editable={editable}
          onContentChange={onContentChange}
          footer={footer}
          placeholder={placeholder}
          showSearch={showSearch}
        />
      )}
    </div>
  );

  if (cardLabel) {
    return (
      <ServiceCard
        label={cardLabel}
        icon={cardIcon}
        enabled
        enabledLabel={langLabel}
        statusPreset="statusCode"
      >
        {body}
      </ServiceCard>
    );
  }

  return body;
};

export default CodeViewer;
